import { Alert, Box, Button, JsonInput, TextInput } from '@mantine/core';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { WithApiKey } from '../wrappers/WithApiKey';
import { License, insert, update } from '../api/licenses';
import { isLeft } from 'fp-ts/lib/Either';
import { toast } from 'react-toastify';
import { WithApp } from '../wrappers/WithApp';

type AppFormData = {
  matchData: Record<string, string>;
  returnData: Record<string, any> | null;
  returnDataString: string;
  valid: boolean;
};

const omit = (keys: string[], obj: Record<string, any>) => {
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (!keys.includes(key)) {
      result[key] = obj[key];
    }
  }
  return result;
};

export const LicenseForm = ({
  keyId,
  license,
}: {
  keyId: string;
  license: License | null;
}) => {
  const [licenseData, setLicenseData] = useState<AppFormData>({
    matchData: license?.matchData ?? {},
    returnData: license?.returnData ?? {},
    returnDataString: license?.returnDataString ?? '',
    valid: true,
  });

  const auth = useAuth();
  const navigate = useNavigate();

  const handleInputChange =
    (name: string) => (e: ChangeEvent<HTMLInputElement>) =>
      setLicenseData({
        ...licenseData,
        matchData: { ...licenseData.matchData, [name]: e.target.value },
      });

  const handleDataChange = (value: string) => {
    try {
      setLicenseData({
        ...licenseData,
        returnData: JSON.parse(value),
        returnDataString: value,
        valid: true,
      });
    } catch (e) {
      setLicenseData({
        ...licenseData,
        returnData: {},
        returnDataString: value,
        valid: false,
      });
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth?.token || !keyId) {
      toast.error('Missing required user details. Please try again later');
      return;
    }
    if (!licenseData.valid) {
      toast.error('App data is invalid. Please update and try again.');
      return;
    }

    const record = omit(['returnDataString', 'valid'], licenseData);

    const result = license
      ? await update(auth.token, keyId, license._id, record)
      : await insert(auth.token, keyId, record);

    if (isLeft(result)) {
      toast.error(result.left.message);
      return;
    }

    navigate(`/api-keys/${encodeURIComponent(keyId)}/licenses`);
  };

  return (
    <WithApiKey id={keyId}>
      {(key) => (
        <WithApp shortCode={key.scope} optional={true}>
          {(app) => (
            <form onSubmit={save}>
              {app?.requiredLicenseFields.length === 0 ? (
                <Alert color="blue">
                  No editable fields for this application type
                </Alert>
              ) : (
                app?.requiredLicenseFields.map((key) => (
                  <TextInput
                    key={key}
                    label={key}
                    name={key}
                    required
                    value={licenseData.matchData[key] ?? ''}
                    onChange={handleInputChange(key)}
                  />
                ))
              )}

              <JsonInput
                label="Returned data"
                name="returnData"
                description="This data will be returned to the caller when the license is successfully validated."
                onChange={handleDataChange}
                value={licenseData.returnDataString}
                flex={1}
                rows={4}
                mt={12}
                validationError="Invalid JSON"
              ></JsonInput>

              <Box mt="md">
                <Button type="submit">
                  {license ? 'Save License' : 'Add License'}
                </Button>
              </Box>
            </form>
          )}
        </WithApp>
      )}
    </WithApiKey>
  );
};
